import {
  CountryIso3166,
  DisplaySettings,
  LangageIso6391,
  MaterialType,
} from "@tfg/db"
import type { Prisma } from "@tfg/db"

import { CertificateStatus } from "@tfg/types/constants/certificates.constants"
import { ProductSheetComponent } from "@tfg/types/schemas/communication-studio/components"

export const NOT_ENTERED_YET = "not-entered-yet"
export const NOT_VALIDATED_YET = "not-validated-yet"

export type NotEnteredYet = typeof NOT_ENTERED_YET
export type NotValidatedYet = typeof NOT_VALIDATED_YET
export type MissingInfoCause = NotEnteredYet | NotValidatedYet

type NonApplicableCause =
  | "irrelevant-with-leather"
  | "irrelevant-with-duvet"
  | "missing-main-component"
  | "maincomponent-cant-be-accessory"
  | "maincomponent-cant-be-wood"
  | "not-applicable"
export type MissingCountryCause = MissingInfoCause | NonApplicableCause

export type CountryInfo<C extends MissingCountryCause> =
  | {
      country: CountryIso3166
      cause?: never
    }
  | {
      country?: never
      cause: C
    }

export interface AgecMaterialDetails {
  type: MaterialType
  proportion: number
  itemId: number
  translations: ProductSheetTranslations
}

export interface AgecOperationDetails {
  id: string
  type: "product" | "component" | "material"
  country: CountryInfo<MissingInfoCause>
  activityName: string
  itemId: number
  childrenRef: string[]
  certificates: AgecCertificateDetails[]
}

export interface AgecCertificateDetails {
  name: string
  standardId: number
  status: CertificateStatus
}

export interface AgecComponentDetails {
  type: string
  itemId: number
  materials: AgecMaterialDetails[]
  certificates: unknown[]
  translations: ProductSheetTranslations
}
export interface OrganizationPublicConfig {
  name: string
  id: string
  config: {
    displayCustomAgecPage?: boolean | undefined
    displaySetting?:
      | (DisplaySettings & {
          components: ProductSheetComponent[]
          languages: LangageIso6391[]
        })
      | undefined
  }
}

export interface PublicDisplaySettings {
  id: string
  slug: string
  outputLogoUrl?: string | undefined
  outputBackgroundColor?: string | undefined
  outputHeaderBackgroundColor?: string | undefined
  outputTextBodyColor?: string | undefined
  outputTitlesColor?: string | undefined
  outputFontFamily?: string | undefined
  filters: Prisma.JsonValue
  organizationSlug: string
  languages: LangageIso6391[]
}

export enum ProductSheetLanguages {
  EN = "EN",
  FR = "FR",
  DE = "DE",
  IT = "IT",
  ES = "ES",
  NL = "NL",
}

export type ProductSheetTranslations = {
  id: number
  originalText: string
} & Partial<Record<ProductSheetLanguages, string>>

export type AgecActivityCountry = {
  activityName: string | "error"
  country: CountryInfo<MissingCountryCause>
  rank: number | null
}

export type AgecActivityCountries = AgecActivityCountry[]

export const RecycledMaterialPercentageCause = {
  BY_INGESTED_PRODUCT_RECYCLED_PERCENTAGE:
    "by-ingested-product-recycled-percentage",
  BY_INGESTED_MATERIAL_COMPOSITION_RECYCLED_PERCENTAGE:
    "by-ingested-material-composition-recycled-percentage",
  BY_ENTERED_COMPONENTS_RECYCLED_PERCENTAGE:
    "by-entered-components-recycled-percentage",
  BY_MIXED_COMPONENTS_RECYCLED_PERCENTAGE:
    "by-mixed-components-recycled-percentage",
  NOT_CALCULATED: "not-calculated",
  ERROR: "error",
} as const

export type RecycledMaterialPercentageCause =
  (typeof RecycledMaterialPercentageCause)[keyof typeof RecycledMaterialPercentageCause]

export const MetalloplasticPercentageCause = {
  BY_INGESTED_PRODUCT_METALLOPLASTIC_PERCENTAGE:
    "by-ingested-product-metalloplastic-percentage",
  BY_INGESTED_MATERIAL_COMPOSITION_METALLOPLASTIC_PERCENTAGE:
    "by-ingested-material-composition-metalloplastic-percentage",
  BY_ENTERED_COMPONENTS_METALLOPLASTIC_PERCENTAGE:
    "by-entered-components-metalloplastic-percentage",
  BY_MIXED_COMPONENTS_METALLOPLASTIC_PERCENTAGE:
    "by-mixed-components-metalloplastic-percentage",
  NOT_CALCULATED: "not-calculated",
  ERROR: "error",
} as const

export type MetalloplasticPercentageCause =
  (typeof MetalloplasticPercentageCause)[keyof typeof MetalloplasticPercentageCause]

export type RecycledFibers = {
  percentage: number
  cause: RecycledMaterialPercentageCause
}

export type MetalloplasticPercentage = {
  percentage: number
  cause: MetalloplasticPercentageCause
}

export type EcoModulationDisplayMention = {
  code: string
  kind: "bonus" | "malus"
}

export type EcoModulationDisplay = {
  mentions: EcoModulationDisplayMention[]
}

export namespace AGECResponses {
  export type GenericAgecData = {
    agecActivityCountriesByComponentsAndMaterials: {
      [componentId: number]: {
        [materialId: number]: AgecActivityCountries
      }
    }
    productSheetGroupId: string
    organizationName: string
    type: string
    brand: string | null
    productAttributes: {
      brand: string | null
      name: string
      color: string | null
      collection: string | null
      brandReference: string
      purchaseOrderReference: string | null
    }
    withPrimaryPackaging: boolean
    primaryPackagingRecyclable: boolean
    primaryPackagingRecycledPercentage: number | null
    primaryPackagingHazardousSubstances: boolean
    operationChainName: string
    operations: AgecOperationDetails[]
    recycledFibers: RecycledFibers
    metalloplasticPercentage: MetalloplasticPercentage
    picture: string | null
    releasesMicroPlastic: boolean | "missing-main-component"
    agecActivityCountries: AgecActivityCountries
    lastUpdate: string
    components: AgecComponentDetails[]
    ecoScoreData: {
      sources: Record<string, EcoScoreData | null>
      availableSources: string[]
    } | null
    ecoModulation?: EcoModulationDisplay | null
  }
}

export type EcoScoreData = {
  ecoScoreCost: number | null
  ecoScoreCostPer100g: number | null
  percentFilled?: number | null
}

export interface PackagingInfo {
  primaryPackagingRecycledPercentage: number | null
  withPrimaryPackaging: boolean
  primaryPackagingRecyclable: boolean
}
